/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useRef, useEffect } from 'react';
import { StyleSheet, View, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

import SociollaText from '@components/sociolla/SociollaText';
import SociollaCard from '@components/sociolla/SociollaCard';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';

const Skeleton = ({ width, height, style }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => {
      animation.stop();
    };
  }, [animatedValue]);

  const shimmerTranslateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-400, 400],
  });

  const opacity = animatedValue.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.4, 0.8, 0.4],
  });

  return (
    <View
      style={[
        {
          width,
          height,
          borderRadius: 4,
          backgroundColor: Colors.grey0,
          overflow: 'hidden',
        },
        style,
      ]}>
      <Animated.View
        style={{
          flex: 1,
          backgroundColor: Colors.grey0,
          opacity,
        }}
      />
      <Animated.View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transform: [{ translateX: shimmerTranslateX }],
        }}>
        <LinearGradient
          colors={['transparent', 'rgba(255,255,255,0.6)', 'transparent']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{
            flex: 1,
            width: 400,
          }}
        />
      </Animated.View>
    </View>
  );
};

const renderSumText = (x, i, textColor) => {
  const isValueEmpty =
    x.value === '' || x.value === null || x.value === undefined;
  const isLongText = x.sum && x.sum?.toString()?.length >= 12;

  let fontSize;
  if (isValueEmpty) {
    if (isWeb) {
      fontSize = isLongText ? 28 : 32;
    } else {
      fontSize = isLongText ? 14 : 18;
    }
  } else {
    if (isWeb) {
      fontSize = 20;
    } else {
      fontSize = isLongText ? 12 : 14;
    }
  }

  const positionStyle = isValueEmpty
    ? { top: 8 }
    : {
        marginTop: !isWeb && i > 1 ? 9 : 3,
      };

  return (
    <SociollaText
      type="medium"
      ellipsizeMode="tail"
      numberOfLines={1}
      currency={true}
      noWrap
      style={{
        color: textColor[i],
        fontSize,
        ...positionStyle,
      }}>
      {x.sum}
    </SociollaText>
  );
};

const CardTotal = ({ data, onPress, loading = false }) => {
  const textColor = {
    0: '#3346AF',
    1: '#FCB21E',
    2: '#00B5DF',
    3: '#00897B',
    4: '#9C27B0',
  };
  const cardCount = data.length || 4;
  const widthCard = isWeb ? `calc((100% / ${cardCount}) - 28px)` : '48%';
  const cardSizeStyle = isWeb
    ? {
        width: widthCard,
        minWidth: widthCard,
        maxWidth: widthCard,
      }
    : {
        width: widthCard,
      };

  return (
    <>
      {data.map((x, i) => {
        const hasSum = !!x.withSum;
        return (
          <SociollaCard
            key={i}
            title={loading ? undefined : x.title}
            header={
              loading ? (
                <Skeleton
                  width={isWeb ? '60%' : '70%'}
                  height={isWeb ? 16 : 18}
                  style={{
                    marginBottom: isWeb ? 16 : 10,
                    borderRadius: 4,
                  }}
                />
              ) : undefined
            }
            borderColor={Colors.blue3}
            titleStyle={styles.titleStyle}
            style={{
              minHeight: isWeb ? 140 : 160,
              flexGrow: 0,
              flexShrink: 0,
              ...cardSizeStyle,
            }}
            onPress={() => (onPress ? onPress(x) : false)}>
            <View
              style={[
                styles.recapContent,
                {
                  bottom: isWeb
                    ? hasSum &&
                      (x.value === '' ||
                        x.value === null ||
                        x.value === undefined)
                      ? 52
                      : hasSum
                      ? 20
                      : 52
                    : hasSum &&
                      (x.value === '' ||
                        x.value === null ||
                        x.value === undefined)
                    ? 18
                    : i > 1
                    ? 22
                    : 18,
                },
              ]}>
              {loading ? (
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}>
                  <Skeleton
                    width={isWeb ? '60%' : '70%'}
                    height={isWeb ? 36 : 32}
                    style={{ marginTop: 8, borderRadius: 4 }}
                  />
                  {hasSum && (
                    <Skeleton
                      width={isWeb ? '50%' : '60%'}
                      height={isWeb ? 20 : 14}
                      style={{
                        marginTop: !isWeb && i > 1 ? 9 : 3,
                        borderRadius: 4,
                      }}
                    />
                  )}
                </View>
              ) : (
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                  }}>
                  {x.value !== '' && x.value !== null && x.value !== undefined && (
                    <SociollaText
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      type="medium"
                      noWrap
                      style={{
                        color: textColor[i],
                        fontSize: isWeb ? 36 : 32,
                        top: 8,
                      }}>
                      {x.value}
                    </SociollaText>
                  )}
                  {x.withSum && renderSumText(x, i, textColor)}
                </View>
              )}
            </View>
          </SociollaCard>
        );
      })}
    </>
  );
};

const styles = StyleSheet.create({
  recapContent: {
    position: 'absolute',
    left: 15,
    right: 15,
    flex: 1,
    flexDirection: isWeb ? 'row' : 'column',
    justifyContent: 'space-between',
  },
  titleStyle: {
    color: Colors.black,
    fontSize: isWeb ? 13 : 16,
    lineHeight: 24,
    marginBottom: isWeb ? 16 : 10,
  },
});

export default React.memo(CardTotal, (prev, next) => prev == next);
